/* eslint-disable react/style-prop-object */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import reduxForm from 'redux-form/es/reduxForm';
import Field from 'redux-form/es/Field';
import actions from 'redux-form/es/actions';
import formValueSelector from 'redux-form/es/formValueSelector';
import validator from 'validator';
import Button from 'components/common/Button';
import InputRadioBox from 'components/Credit24/Core/Controls/InputRadioBox';
import InputField from 'components/Credit24/Core/Controls/InputField';
import {
  fromCents,
  getCurrencyCode,
  getMinimumFractionDigits,
  isMobile,
  keyErrorsFieldRequired,
  toCents,
} from 'utils/helpers';

const { clearFields } = actions;

const amountItems = [0.2, 0.4, 0.6, 0.8, 1];

const roundToHigherTen = (num) => ((parseInt(num / 10, 10) + 1) * 10);

const propTypes = {
  handleSubmit: PropTypes.func,
  contract: PropTypes.object,
  onWithdrawalClick: PropTypes.func,
  dispatch: PropTypes.func,
  withDrawalAmountRadioValue: PropTypes.string,
};

class WithdrawalForm extends Component {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.scrollToForm = this.scrollToForm.bind(this);
    this.onDiscreteAmountFocus = this.onDiscreteAmountFocus.bind(this);
    this.onCustomAmountEntered = this.onCustomAmountEntered.bind(this);
  }

  onSubmit() {
    this.props.onWithdrawalClick();
  }

  onCustomAmountEntered() {
    if (typeof this.props.withDrawalAmountRadioValue !== 'undefined') {
      this.props.dispatch(clearFields('withdrawalForm', false, false, 'withdrawalAmountBox'));
    }
  }

  onDiscreteAmountFocus() {
    this.props.dispatch(clearFields('withdrawalForm', false, false, 'withdrawalCustomAmountBox'));
  }

  getAvailableAmounts(availableLimit) {
    if (availableLimit <= 50) {
      return [availableLimit];
    }

    const availableAmounts = amountItems.map((item) => {
      let amount = availableLimit * item;
      if (amount % 10 !== 0) {
        amount = roundToHigherTen(amount);
      }
      return Math.min(amount, availableLimit);
    });

    return availableAmounts;
  }

  scrollToForm() {
    const viewportOffset = this.refs.form.getBoundingClientRect();
    if (viewportOffset.top < 0) {
      window.scrollTo(0, (viewportOffset.top - 64) + window.scrollY);
    }
  }

  render() {
    const { handleSubmit, contract } = this.props;
    const availableLimit = fromCents(contract.availableLimit);
    const availableAmounts = this.getAvailableAmounts(availableLimit);
    return (
      <form onSubmit={handleSubmit(this.onSubmit)} ref="form">
        <div className="grid">
          {availableAmounts.map((value) => (
            <div className="grid__item one-half" key={value}>
              <Field
                key={value}
                title={(
                  <FormattedNumber
                    style="currency"
                    currency={getCurrencyCode()}
                    value={value}
                    minimumFractionDigits={getMinimumFractionDigits(value, 2)}
                    maximumFractionDigits={2}
                  />
                )}
                name="withdrawalAmountBox"
                component={InputRadioBox}
                val={`${toCents(value, 'float')}`}
                isFullWidth
                onFocus={this.onDiscreteAmountFocus}
              />
            </div>
          ))}
          {isMobile && (
            <div className="grid__item one-half">
              <Field
                title={<FormattedMessage id="selfservice.withdrawalForm.inputs.otherAmount" />}
                name="withdrawalCustomAmountBox"
                component={InputField}
                isFullWidth
                isErrorVisible
                onChange={this.onCustomAmountEntered}
                type="tel"
              />
            </div>
          )}
        </div>
        <div className="grid grid--center">
          <div className="grid__item one-half palm-one-whole">
            <Button onClick={this.scrollToForm} isFullWidth>
              <FormattedMessage id="selfservice.withdrawalForm.buttons.withdraw" />
            </Button>
          </div>
        </div>
      </form>
    );
  }
}

WithdrawalForm.propTypes = propTypes;

const withdrawalFormConfig = {
  form: 'withdrawalForm',
  validate: (values, { contract }) => {
    const errors = {};
    if (!values.withdrawalAmountBox && !values.withdrawalCustomAmountBox) {
      errors.withdrawalAmountBox = keyErrorsFieldRequired;
    } else if (values.withdrawalCustomAmountBox) {
      if (!validator.matches(values.withdrawalCustomAmountBox, /^\d+(\.\d{2})?$/)) {
        errors.withdrawalCustomAmountBox = 'errors.field.digits';
        return errors;
      }
      const customAmount = parseFloat(values.withdrawalCustomAmountBox);
      const max = fromCents(contract.availableLimit);
      if (customAmount <= 0) {
        errors.withdrawalCustomAmountBox = 'errors.field.digits';
      } else if (customAmount > max) {
        errors.withdrawalCustomAmountBox = 'errors.field.digits.tooLarge';
      } else if (max >= 10 && customAmount !== max && customAmount % 10 !== 0) {
        errors.withdrawalCustomAmountBox = 'errors.field.digits.only.ten';
      } else if (max < 10 && customAmount !== max) {
        errors.withdrawalCustomAmountBox = 'errors.field.digits.maxAmountOnly';
      }
    }
    return errors;
  },
  onSubmitFail: (error) => {
    if (DEBUG) {
      console.warn('form submit fail', error);
    }
  },
};

const selector = formValueSelector('withdrawalForm');

export default connect(
  (state, props) => ({
    initialValues: {
      withdrawalAmountBox: `${props.contract.availableLimit}`,
    },
    withDrawalAmountRadioValue: selector(state, 'withdrawalAmountBox'),
  })
)(reduxForm(withdrawalFormConfig)(WithdrawalForm));
